/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import {
  daySystemLocalEpoch,
  MaxTime,
  Simulator,
  SimulatorAt
} from '@ki-wi/deterministic';

import { IteratorFactory } from './iterator-factory';

/* eslint-env browser */
/* eslint-disable max-statements */
/* eslint-disable no-await-in-loop */

/**
 * @import { SpotContent, Spot } from '.'
 * @import { Playlist } from '../../playlist/playlist'
 * @import UnixTimestamp from '../../unix-timestamp/unix-timestamp'
 * @import { Time } from '@ki-wi/deterministic';
 */

export class RealtimePlaylistSimulator {
  /**
   * @param {boolean} disableAutoRewind
   * @param {() => Time} nowNs
   * @param {Playlist} playlist
   * @param {number} parentVolume
   * @param {UnixTimestamp} unixTimestamp
   */
  constructor(disableAutoRewind, nowNs, playlist, parentVolume, unixTimestamp) {
    this.disableAutoRewind = disableAutoRewind;
    this.nowNs = nowNs;
    this.unixTimeOffset = unixTimestamp.offset();
    const clipIteratorFactory = IteratorFactory.fromPlaylist(playlist, {
      volume: parentVolume
    });

    /** @type {InstanceType<typeof Simulator<SpotContent, string>>} */
    this.simulator = new Simulator(clipIteratorFactory, nowNs(), MaxTime);
  }

  /**
   * @returns {Promise<Spot<SpotContent | null> | undefined>}
   */
  async next() {
    return (
      (await getNextSpot(this.nowNs, this.simulator, this.disableAutoRewind)) ??
      undefined
    );
  }

  /**
   * @returns {Promise<Spot<SpotContent | null> | undefined>}
   */
  async peek() {
    const s = this.simulator.clone();
    return (
      (await getNextSpot(this.nowNs, s, this.disableAutoRewind)) ?? undefined
    );
  }
}

/**
 * @param {() => Time} nowNs
 * @param {Simulator} simulator
 * @param {boolean} disableAutoRewind
 * @returns {Spot<SpotContent | null> | null}
 */
function getNextSpot(nowNs, simulator, disableAutoRewind) {
  simulator.time = nowNs();
  /** @type {Spot<SpotContent | null> | null} */
  let spot = simulator.next();
  // disableAutoRewind === true - konečný playlist
  if (disableAutoRewind) {
    return spot;
  }
  // disableAutoRewind === false - nekonečný playlist, pokud přijde zarážka, tak zkusím další volání next
  if (spot && spot.content === null && spot.duration === 0) {
    spot = simulator.next();
  }
  return spot;
}

export class DeterministicPlaylistSimulator {
  /**
   * @param {Playlist} playlist
   * @param {number} parentVolume
   * @param {UnixTimestamp} unixTimestamp
   */
  constructor(playlist, parentVolume, unixTimestamp) {
    this.unixTimestamp = unixTimestamp;
    this.unixTimeOffset = unixTimestamp.offset();
    const clipIteratorFactory = IteratorFactory.fromPlaylist(playlist, {
      volume: parentVolume
    });

    /** @type {InstanceType<typeof SimulatorAt<SpotContent, string>>} */
    this.simulatorAt = new SimulatorAt(
      clipIteratorFactory,
      daySystemLocalEpoch
    );
    this.now = this.now.bind(this);
  }

  /**
   * @returns {Promise<Spot<SpotContent | null> | undefined>}
   */
  async next() {
    this.unixTimeOffset = this.unixTimestamp.offset();
    const time = this.now();
    const spot = (await this.simulatorAt.getAt(time)) ?? undefined;
    return spot;
  }

  /**
   * @param {Spot} spot
   * TODO: vyřešit nutnost předávání spot. Získání aktuálního spotu z next
   * metody způsobí neočekávané přehrávní.
   * @returns {Promise<Spot<SpotContent | null> | undefined>}
   */
  async peek(spot) {
    const time = /** @type {Time} */ (spot.time + spot.duration);
    return (await this.simulatorAt.getAt(time)) ?? undefined;
  }

  /**
   * Zjistí aktuální čas.
   * @private
   * @returns {Time} Aktuální čas v mikrosekundách.
   */
  now() {
    return /** @type {Time} */ (
      (this.unixTimestamp.now() + this.unixTimeOffset) * 1000
    );
  }
}
